/**
 * Pre-approved legal / consent strings for dynamic forms.
 *
 * Forms rendered by `render_ui` are composed by the model at runtime, which is
 * fine for labels and placeholders but NOT for consent and privacy wording —
 * legally-reviewed copy must not be re-authored (or re-translated) per session.
 *
 * The `LegalText` catalog component renders these by KEY: the model chooses
 * WHICH clause appears, never its text. Add new clauses here after review.
 *
 * @module lib/json-render/legal-strings
 */

/** Locale → text for one legal clause. */
type LegalStringEntry = Record<string, string>;

/**
 * The approved clause library. Keys are stable and referenced from model-authored
 * specs; changing a key is a breaking change for any prompt that names it.
 */
export const LEGAL_STRINGS: Record<string, LegalStringEntry> = {
  privacy_consent: {
    en: "I consent to my personal data being collected and processed for the purpose of handling this request, in line with the privacy policy.",
    ar: "أوافق على جمع بياناتي الشخصية ومعالجتها لغرض التعامل مع هذا الطلب، وفقًا لسياسة الخصوصية.",
  },
  contact_consent: {
    en: "I agree to be contacted by phone or message about this request.",
    ar: "أوافق على التواصل معي عبر الهاتف أو الرسائل بخصوص هذا الطلب.",
  },
  unconfirmed_notice: {
    en: "Submitting this form does not confirm your request. A representative will contact you to confirm the details.",
    ar: "إرسال هذا النموذج لا يعني تأكيد طلبك. سيتواصل معك أحد الممثلين لتأكيد التفاصيل.",
  },
  data_accuracy: {
    en: "I confirm the information provided above is accurate to the best of my knowledge.",
    ar: "أؤكد أن المعلومات المقدمة أعلاه صحيحة على حد علمي.",
  },
};

/**
 * Resolves a legal clause for a locale, falling back to English and then to an
 * empty string. Never throws — an unknown key renders nothing rather than
 * breaking the form.
 *
 * @param key - Clause key from {@link LEGAL_STRINGS}
 * @param locale - Preferred locale (e.g. "ar"); falls back to "en"
 * @returns The approved text, or "" when the key is unknown
 */
export function getLegalString(key: string, locale?: string | null): string {
  const entry = LEGAL_STRINGS[key];
  if (!entry) return "";
  if (locale && entry[locale]) return entry[locale];
  return entry.en ?? Object.values(entry)[0] ?? "";
}
